/**
 * @fileoverview Connect React subscriptions and imperative request channels to `dataApi.js`'s identity-scoped metadata cache. Authentication supplies cache scope; components own their selected resource and response application. Shared invalidation keeps multiple workspaces aligned. Transport/request lifetime and canonical server identity remain separate, and binary/editor state stays outside this boundary.
 */

import { useContext, useEffect, useMemo } from 'react';
import { batch, ReactReduxContext } from 'react-redux';
import { AuthContext } from '../../AuthContext';
import { createAppStore } from '../store';
import { documentDataApi, documentResourceUrl, documentResourceRevision, DOCUMENT_FRESH_SECONDS, rememberDocumentView } from './dataApi';



export function createDocumentDataClient(reduxStore, scope) {
  const channels = new Map();
  const args = 

  (url, options = {}) => ({ scope, url, ...(options.pageSize ? { pageSize: options.pageSize, pages: options.pages || 1 } : {}) });
  const read = 

  (url, options) => documentDataApi.endpoints.resource.select(args(url, options))(reduxStore.getState()).data;
  const stop = 

  (channel) => {
    const previous = channels.get(channel);
    channels.delete(channel);
    previous?.unsubscribe();
    // A query may be shared by another view, or revisited before it finishes.
    // Detach this view's callback; RTK owns the request and its timeout.
    previous?.request?.unsubscribe();
  };
  return {
    read,
    stop,
    

    close() { [...channels.keys()].forEach((channel) => stop(channel)); },
    

    view(key) { return reduxStore.getState().documentDataSession.views[`${scope}:${key}`]; },
    

    remember(key, value) { reduxStore.dispatch(rememberDocumentView({ key: `${scope}:${key}`, value })); },
    

    load(channel, url, onData, { force = false, onError = () => {}, onClear = () => {}, ...options } = {}) {
      const arg = args(url, options);
      stop(channel);
      let previous = documentDataApi.endpoints.resource.select(arg)(reduxStore.getState());
      let lastData = previous.data;
      if (lastData !== undefined) batch(() => onData(lastData, { cached: true }));
      const entry = { url: documentResourceUrl(url) };
      channels.set(channel, entry);
      const publish = 

      () => {
        if (channels.get(channel) !== entry) return;
        const next = documentDataApi.endpoints.resource.select(arg)(reduxStore.getState());
        if (next === previous) return;
        previous = next;
        if (next.data !== lastData) {
          lastData = next.data;
          batch(() => {
            if (lastData !== undefined) onData(lastData, { cached: false });
            else onClear();
          });
        }
        if (next.isError) batch(() => onError({ ...next.error, response: { status: next.error?.status, data: next.error?.data } }));
      };
      entry.unsubscribe = reduxStore.subscribe(publish);
      entry.request = reduxStore.dispatch(documentDataApi.endpoints.resource.initiate(arg, {
        forceRefetch: force || (lastData && lastData.cacheRevision !== documentResourceRevision(reduxStore.getState().documentDataSession, url))
          ? true : DOCUMENT_FRESH_SECONDS,
        subscriptionOptions: { refetchOnFocus: true, refetchOnReconnect: true },
      }));
      // Subscription callbacks own delivery, so background refetches update
      // the same view and switching channels discards obsolete callbacks.
      const finish = async () => {
        let data = await entry.request.unwrap();
        // RTK deduplicates requests while another page is in flight. A caller
        // asking for more pages must still fetch the remainder after it settles.
        while (channels.get(channel) === entry && options.pageSize
          && data?.pagination?.has_next && (data.cachedPages || 1) < (options.pages || 1)) {
          entry.request.unsubscribe();
          entry.request = reduxStore.dispatch(documentDataApi.endpoints.resource.initiate(arg));
          data = await entry.request.unwrap();
        }
        return data;
      };
      return finish().catch((error) => {
        throw Object.assign(new Error(error.message || 'Could not load document data.'), error, {
          response: { status: error.status, data: error.data },
        });
      });
    },
    

    key(url) { return documentResourceUrl(url); },
  };
}



export default function useDocumentData() {
  const context = useContext(ReactReduxContext);
  const auth = useContext(AuthContext);
  // Standalone previews/tests may have no app Provider. Never share their data
  // through an implicit global store across unrelated sessions.
  const reduxStore = useMemo(() => context?.store || createAppStore(), [context?.store]);
  const scope = String(auth?.employeeId ?? 'authenticated-session');
  const client = useMemo(() => createDocumentDataClient(reduxStore, scope), [reduxStore, scope]);
  useEffect(() => () => client.close(), [client]);
  return client;
}
